import { useEffect, useState } from 'react'
import { Gift, Share2, Copy, Info, CheckCircle } from 'lucide-react'

interface InviteReward {
  id: string
  description: string
  source: string
  status: 'pending' | 'applied' | 'expired'
  createdAt?: string
  appliedAt?: string
  expiresAt?: string
}

import { useAuth } from '@/hooks/useAuth'
import { api } from '@/lib/api'

export default function InviteFriendsPage() {
  const { user, getToken } = useAuth()
  const [rewards, setRewards] = useState<InviteReward[]>([])
  const [stats, setStats] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentInviteCode, setCurrentInviteCode] = useState<string | null>(null)
  const [currentInviteMessage, setCurrentInviteMessage] = useState<string | null>(null)
  const [currentInviteLink, setCurrentInviteLink] = useState<string | null>(null)

  useEffect(() => {
    if (user?.type === 'jobseeker') {
      fetchInvites()
    }
  }, [user])

  const fetchInvites = async () => {
    try {
      setIsLoading(true)
      const token = await getToken()
      if (!token) return

      const response = await api.getMyInvites(token)
      if (response.success) {
        setRewards(response.rewards || [])
        setStats(response.stats)
        setCurrentInviteCode(response.currentInviteCode)
        setCurrentInviteMessage(response.currentInviteMessage)
        setCurrentInviteLink(response.currentInviteLink)
      }
    } catch (error) {
      console.error('Error fetching invites:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleApplyReward = async (reward: InviteReward) => {
    if (window.confirm(`Apply ${reward.description}? This will upgrade your subscription immediately.`)) {
      try {
        const token = await getToken()
        if (!token) return

        const response = await api.applyReward(reward.id, token)
        if (response.success) {
          alert('Reward applied successfully! Enjoy your premium features.')
          await fetchInvites()
        } else {
          throw new Error(response.error || 'Failed to apply reward')
        }
      } catch (error) {
        console.error('Error applying reward:', error)
        alert('Failed to apply reward')
      }
    }
  }

  const handleShareInvite = async () => {
    if (!currentInviteMessage) {
      alert('No invite code available. Please try again.')
      return
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join ReferPal',
          text: currentInviteMessage,
        })
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Error sharing:', error)
        }
      }
    } else {
      // Fallback: copy to clipboard
      await copyInviteMessage()
    }
  }

  const copyInviteMessage = async () => {
    if (!currentInviteMessage) {
      alert('No invite message available. Please try again.')
      return
    }

    try {
      await navigator.clipboard.writeText(currentInviteMessage)
      alert('Invite message copied to clipboard')
    } catch (error) {
      console.error('Error copying invite message:', error)
      alert('Failed to copy invite message')
    }
  }

  const copyInviteCode = async () => {
    if (currentInviteCode) {
      try {
        await navigator.clipboard.writeText(currentInviteCode)
        alert('Invite code copied to clipboard')
      } catch (error) {
        console.error('Error copying:', error)
      }
    } else {
      alert('No invite code available')
    }
  }

  const copyInviteLink = async () => {
    if (currentInviteLink) {
      try {
        await navigator.clipboard.writeText(currentInviteLink)
        alert('Invite link copied to clipboard')
      } catch (error) {
        console.error('Error copying:', error)
      }
    } else {
      alert('No invite link available')
    }
  }

  const getProgressPercentage = () => {
    if (!stats) return 0
    return Math.min((stats.verifiedReferrers / 3) * 100, 100)
  }

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return 'Invalid date'
      return date.toLocaleDateString()
    } catch (error) {
      return 'Invalid date'
    }
  }

  if (user?.type !== 'jobseeker') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Info className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Job Seekers Only</h2>
          <p className="text-gray-600">
            The invite and reward program is available for job seekers only.
            Referrers already have unlimited access to all features.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-6">Invite & Rewards</h1>
      
      {/* Progress Card */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-xl p-6 sm:p-8 text-white mb-6">
        <div className="flex items-center mb-2">
          <Gift className="h-6 w-6" />
          <h2 className="ml-2 text-2xl font-bold">Get Premium Free!</h2>
        </div>
        
        <p className="text-white text-opacity-90 mb-6">
          Invite 3 friends to become referrers and get verified to earn 1 month of premium!
        </p>

        <div className="mb-2">
          <div className="bg-white bg-opacity-20 rounded-full h-2 overflow-hidden">
            <div 
              className="bg-green-400 h-full transition-all duration-300"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <p className="text-white text-opacity-90 text-sm mt-2">
            {stats?.verifiedReferrers || 0} / 3 verified referrers
          </p>
        </div>

        {stats?.isEligibleForReward && (
          <div className="flex items-center bg-white bg-opacity-20 rounded-lg px-3 py-2 inline-flex">
            <CheckCircle className="h-4 w-4 text-green-300" />
            <span className="ml-2 text-sm font-medium">Eligible for reward!</span>
          </div>
        )}
      </div>

      {/* Pending Rewards */}
      {rewards.filter(r => r.status === 'pending').length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Available Rewards</h2>
          <div className="space-y-3">
            {rewards.filter(r => r.status === 'pending').map((reward) => (
              <div key={reward.id} className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start flex-1">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Gift className="h-5 w-5 text-amber-500" />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="font-medium text-gray-900">{reward.description}</p>
                      <p className="text-sm text-gray-600 mt-0.5">From: {reward.source}</p>
                      <p className="text-xs text-gray-500 mt-1">Created: {formatDate(reward.createdAt)}</p>
                      {reward.expiresAt && (
                        <p className="text-xs text-amber-600 mt-1">Expires: {formatDate(reward.expiresAt)}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleApplyReward(reward)}
                    disabled={isLoading}
                    className="ml-4 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50 flex-shrink-0"
                  >
                    Apply
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Applied Rewards */}
      {rewards.filter(r => r.status === 'applied').length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Applied Rewards</h2>
          <div className="space-y-3">
            {rewards.filter(r => r.status === 'applied').map((reward) => (
              <div key={reward.id} className="bg-green-50 border border-green-200 rounded-xl p-4 sm:p-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Gift className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="font-medium text-gray-900">{reward.description}</p>
                    <p className="text-sm text-gray-600 mt-0.5">From: {reward.source}</p>
                    <p className="text-xs text-gray-500 mt-1">Applied: {formatDate(reward.appliedAt)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Your Invite Code Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Invite Code</h2>

        {currentInviteCode ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite Code</label>
              <div className="flex items-center bg-gray-50 border-2 border-blue-500 rounded-lg p-3">
                <span className="flex-1 font-bold text-lg tracking-wider text-gray-900">{currentInviteCode}</span>
                <button
                  onClick={copyInviteCode}
                  className="ml-2 p-2 hover:bg-gray-100 rounded transition-colors"
                >
                  <Copy className="h-5 w-5 text-blue-500" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Invite Link</label>
              <div className="flex items-center bg-gray-50 border border-gray-300 rounded-lg p-3">
                <span className="flex-1 text-sm text-gray-600 truncate">{currentInviteLink}</span>
                <button
                  onClick={copyInviteLink}
                  className="ml-2 p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                >
                  <Copy className="h-5 w-5 text-blue-500" />
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleShareInvite}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Share2 className="h-4 w-4 mr-2" />
                <span className="font-medium">Share Link</span>
              </button>
              
              <button
                onClick={copyInviteMessage}
                className="flex-1 flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Copy className="h-4 w-4 mr-2" />
                <span className="font-medium">Copy Message</span>
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
                <p className="text-gray-600">Generating invite code...</p>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Gift className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">No Invite Code Yet</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Generate an invite code to share with friends and earn premium rewards!
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Invite Statistics */}
      {stats && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite Statistics</h2>
          
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{stats.totalInvitesSent}</p>
              <p className="text-sm text-gray-600 mt-1">Sent</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{stats.acceptedInvites}</p>
              <p className="text-sm text-gray-600 mt-1">Accepted</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{stats.verifiedReferrers}</p>
              <p className="text-sm text-gray-600 mt-1">Verified</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-500">{stats.needsMoreReferrers}</p>
              <p className="text-sm text-gray-600 mt-1">Needed</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
